import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow, isValid } from 'date-fns';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import TaskDetails from './TaskDetails';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiAlertCircle, FiClock, FiCheckCircle, FiEye, FiEdit2, FiCheckSquare } = FiIcons;

const TaskList = ({ tasks, onEditTask }) => {
  const { teamMembers, updateTask } = useData();
  const { user, hasPermission } = useAuth();
  const [selectedTask, setSelectedTask] = useState(null);

  const getAssignees = (assignedTo) => {
    if (!assignedTo) return [];
    const ids = Array.isArray(assignedTo) ? assignedTo : [assignedTo];
    return ids.map(id => teamMembers.find(m => String(m.id) === String(id))).filter(Boolean);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'in_progress': return 'text-blue-600';
      case 'pending': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return FiCheckCircle;
      case 'in_progress': return FiClock;
      case 'pending': return FiAlertCircle;
      default: return FiClock;
    }
  };

  const handleStatusChange = (taskId, newStatus) => {
    updateTask(taskId, { status: newStatus });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isValid(date) ? formatDistanceToNow(date, { addSuffix: true }) : '';
  };

  // Permissions Logic
  const canUpdateTask = (task) => {
    const isAssigned = Array.isArray(task.assignedTo) ? task.assignedTo.includes(user.id) : task.assignedTo === user.id;
    return hasPermission('all') || hasPermission('assign_tasks') || (hasPermission('update_tasks') && isAssigned) || task.assignedBy === user.id;
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">Task</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">Assignees</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">Priority</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">Status</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">Deadline</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <AnimatePresence>
                {tasks.map((task, index) => {
                  const assignees = getAssignees(task.assignedTo);
                  const status = task.status || 'pending'; // Safe default

                  return (
                    <motion.tr
                      key={task.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedTask(task)}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1">{task.title}</h4>
                          <p className="text-sm text-gray-600 line-clamp-1">{task.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex -space-x-2">
                          {assignees.length > 0 ? (
                            assignees.slice(0, 3).map(assignee => (
                              <img 
                                key={assignee.id} 
                                src={assignee.avatar || 'https://via.placeholder.com/32'} 
                                alt={assignee.name} 
                                title={assignee.name}
                                className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 object-cover" 
                              />
                            ))
                          ) : (
                            <span className="text-sm text-gray-500 italic">Unassigned</span>
                          )}
                          {assignees.length > 3 && (
                            <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                              +{assignees.length - 3}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        {canUpdateTask(task) ? (
                          <select
                            value={status}
                            onChange={(e) => handleStatusChange(task.id, e.target.value)}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                        ) : (
                          <div className={`flex items-center space-x-1 ${getStatusColor(status)}`}>
                            <SafeIcon icon={getStatusIcon(status)} className="w-4 h-4" />
                            <span className="text-sm capitalize">{String(status).replace('_', ' ')}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1 text-gray-600">
                          <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                          <span className="text-sm">
                            {formatDate(task.deadline)}
                          </span>
                        </div>
                      </td>
                      {/* Actions Column - stopPropagation here prevents row click when clicking empty space in cell */}
                      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end space-x-2">
                          <button 
                            onClick={() => setSelectedTask(task)}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="View Details"
                          >
                            <SafeIcon icon={FiEye} className="w-4 h-4" />
                          </button>
                          {canUpdateTask(task) && (
                            <button 
                              onClick={() => onEditTask(task)}
                              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit Task"
                            >
                              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
        {tasks.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiCheckSquare} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-600">Create your first task or adjust the filters.</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedTask && (
          <TaskDetails 
            task={selectedTask} 
            onClose={() => setSelectedTask(null)} 
            onEdit={(task) => {
              setSelectedTask(null);
              onEditTask(task);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default TaskList;